import {computed, inject, Injectable, signal} from '@angular/core';
import {HttpClient, HttpContext} from '@angular/common/http';
import {catchError, map, Observable, of, tap} from 'rxjs';
import {SILENT_ERRORS} from '../http-context.tokens';
import {AppRole} from '../models/app-role';

interface SessionUser {
  sub: string;
  login: string;
  roles: string[];
}

@Injectable({
  providedIn: 'root'
})
export class AuthApiService {

  private readonly http = inject(HttpClient);

  private readonly _user = signal<SessionUser | null | undefined>(undefined);

  readonly user = this._user.asReadonly();
  readonly isAuthenticated = computed(() => this._user() != null);
  readonly isAdmin = computed(() => {
    const roles = this._user()?.roles ?? [];
    return roles.includes(AppRole.Admin) || roles.includes(AppRole.SuperAdmin);
  });
  readonly isSuperAdmin = computed(() => this._user()?.roles?.includes(AppRole.SuperAdmin) === true);

  /**
   * Returns true if a session cookie identifies a logged-in user. Result is
   * cached for the page lifetime; call refreshSession() to re-check.
   */
  loadSession(): Observable<boolean> {
    const cached = this._user();
    if (cached !== undefined) {
      return of(cached !== null);
    }
    return this.fetchSession();
  }

  refreshSession(): Observable<boolean> {
    this._user.set(undefined);
    return this.fetchSession();
  }

  clearLocal(): void {
    this._user.set(null);
  }

  logout(): void {
    window.location.href = '/api/sso/connect/logout';
  }

  private fetchSession(): Observable<boolean> {
    return this.http.get<SessionUser>('/api/sso/SsoFlow/me',
      { context: new HttpContext().set(SILENT_ERRORS, true) }).pipe(
      tap(u => this._user.set(u)),
      map(() => true),
      catchError(() => {
        this._user.set(null);
        return of(false);
      })
    );
  }
}
